<template>
  <v-layout>
    <v-flex>
      <v-autocomplete
        @input="onInput"
        @update:search-input="getList"
        prepend-inner-icon="search"
        :search-input.sync="searchInput"
        :label="resolvedLabel"
        :items="searchInput ? stateList : addresses"
        return-object
        item-text="description"
        hide-no-data
        outlined
        filled
        dense
        hide-details
        :disabled="disabled">
        <template v-slot:selection="{ item }">{{ item.description || formatAddress(item) }}</template>
        <template v-slot:item="{ item }">
          <v-list-item-icon>
            <v-icon v-if="item.city">mdi-briefcase-outline</v-icon>
            <v-icon v-else>mdi-google-maps</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ item.description || formatAddress(item) }}</v-list-item-title>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-flex>
  </v-layout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { CountryCode } from 'libphonenumber-js/types';
import GooglePlacesApi from '@/modules/googleAutocomplete/services/GooglePlacesApi';
import { Address } from '@/modules/multiForm/shared/MultiformModels';
import GoogleAutocompletePrediction from '@/modules/googleAutocomplete/GoogleAutocompletePrediction';
import { AutocompletePlacesTypes } from '@/modules/googleAutocomplete/enums/AutocompletePlacesTypes';
import { addressFormat } from '@/composables/useFilters';
import { useInstance } from '@/composables/useInstance';

const { $i18n } = useInstance();

const props = withDefaults(defineProps<{
  addresses?: Address[];
  label?: string;
  type?: AutocompletePlacesTypes;
  disabled?: boolean;
  includedRegionCodes?: CountryCode[];
}>(), {
  addresses: () => [],
  type: AutocompletePlacesTypes.CITIES_AND_ROUTES,
  disabled: false,
  includedRegionCodes: () => [],
});

const resolvedLabel = computed(() => props.label ?? $i18n.useAddressAutocomplete.SEARCH_ADDRESS);

const emit = defineEmits<{
  (e: 'input', value: Address): void;
}>();

const searchInput = ref('');
const stateList = ref<GoogleAutocompletePrediction[]>([]);

async function getList(value: string): Promise<void> {
  if (value && value.length > 1) {
    const response = await GooglePlacesApi.getAddressesByInput(
      value,
      props.type,
      props.includedRegionCodes,
    );
    if (response) {
      stateList.value = response;
    }
  }
}

function formatAddress(address: Address): string {
  return addressFormat(address);
}

async function onInput(addressObject: GoogleAutocompletePrediction | Address): Promise<void> {
  if ('place_id' in addressObject && addressObject.place_id) {
    const response = await GooglePlacesApi.getAddress(addressObject.place_id, addressObject.description);
    emit('input', response);
  } else {
    emit('input', addressObject as Address);
    searchInput.value = '';
  }
}
</script>
